import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { FieldGroup } from '@Sage/hoi-poi-ui';
import isTouched from './utils/isTouched';
import CrudField from './CrudField';

const MultipleCrudField = ({
    label,
    labelMode,
    inputs = [],
    data,
    onChange,
    getError,
    errors,
    firstErrorField,
    isBulkAction,
    duplicates,
    dynamicHiddenMap,
    configMap,
    originalData,
    smartFieldOverridesCache,
    fieldsMode = 'vertical',
    entityName,
    environmentField,
    idCurrency,
    idEnvironment,
}) => {
    const inputsEl = useMemo(
        () =>
            inputs.reduce((obj, field) => {
                if (
                    !field.hidden &&
                    !(dynamicHiddenMap && dynamicHiddenMap[field.fieldConfiguration])
                )
                    obj.push(CrudField);

                return obj;
            }, []),
        [dynamicHiddenMap, inputs],
    );
    const inputsProps = useMemo(
        () =>
            inputs.reduce((obj, field) => {
                // Use the smartFieldOverridesCache from parent
                const smartFieldOverrides = smartFieldOverridesCache?.get(field.id) || {};

                const { defaultValue, hidden, ...otherOverrides } = smartFieldOverrides;

                if (
                    hidden ||
                    field.hidden ||
                    (dynamicHiddenMap && dynamicHiddenMap[field.fieldConfiguration])
                )
                    return obj;

                let hintLiteral = field.hint;
                const inputAttrs = field.inputAttrs || {};
                if (field.mandatory) hintLiteral = hintLiteral + ' *';

                // Get parentValue dependency
                let parentValue = null;
                if (inputAttrs && inputAttrs.parentField) {
                    parentValue = data[inputAttrs.parentField];
                }

                const baseProps = {
                    type: field.type,
                    hint: hintLiteral,
                    description: field.description,
                    fieldConfiguration: field.fieldConfiguration,
                    maxLength: field.dataLength,
                    limitInput: field.limitInput,
                    readOnly: field.readOnly,
                    hidden: field.hidden,
                    inputAttrs: inputAttrs,
                    value: data[field.id],
                    fieldId: field.id,
                    parentValue: parentValue,
                    onChange: onChange(field.id),
                    error: getError(errors[field.id], [field.dataLength]),
                    firstErrorField: firstErrorField && field.id.toLowerCase() === firstErrorField,
                    isBulkAction: isBulkAction,
                    duplicates: duplicates,
                    entityName: entityName,
                    environmentField: environmentField,
                    idCurrency: idCurrency,
                    idEnvironment: idEnvironment,
                };

                const hasDefaultValue = !!Object.entries(otherOverrides).length && !!defaultValue;
                const parentFieldId = configMap[field.smartField?.parentFieldId];
                const showSmartFieldDefault =
                    isTouched({ data, originalData, field: parentFieldId }) && hasDefaultValue;

                const finalValue = showSmartFieldDefault ? defaultValue : data[field.id];

                obj.push({
                    ...baseProps,
                    key: `crud-field-${field.id}-index`,
                    value: finalValue,
                    ...otherOverrides,
                });

                return obj;
            }, []),
        [
            configMap,
            data,
            duplicates,
            dynamicHiddenMap,
            errors,
            firstErrorField,
            getError,
            inputs,
            isBulkAction,
            onChange,
            originalData,
            smartFieldOverridesCache,
            entityName,
            environmentField,
            idCurrency,
            idEnvironment,
        ],
    );

    if (inputsProps.length === 0) return null;
    let classes = ['fm-multiple-fields'];

    return (
        <div className={classes.join(' ')}>
            <FieldGroup
                label={label}
                labelMode={labelMode}
                inputs={inputsEl}
                inputProps={inputsProps}
                isFullWidth
                fieldsMode={fieldsMode}
            />
        </div>
    );
};

MultipleCrudField.displayName = 'MultipleCrudField';

MultipleCrudField.propTypes = {
    label: PropTypes.string,
    labelMode: PropTypes.string,
    inputs: PropTypes.array,
    data: PropTypes.object,
    onChange: PropTypes.func,
    getError: PropTypes.func,
    errors: PropTypes.object,
    firstErrorField: PropTypes.string,
    isBulkAction: PropTypes.bool,
    duplicates: PropTypes.object,
    dynamicHiddenMap: PropTypes.object,
    configMap: PropTypes.object,
    originalData: PropTypes.object,
    smartFieldOverridesCache: PropTypes.object,
    fieldsMode: PropTypes.oneOf(['vertical', 'horizontal']),
    entityName: PropTypes.string,
    environmentField: PropTypes.object,
    idCurrency: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idEnvironment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default memo(MultipleCrudField);
